import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

import { usePosProfile } from './PosProfileContext';

/**
 * Dialog for recording cash in/out movements during a shift.
 *
 * - Cash In: adding cash to the drawer (e.g. float top-up, change fund)
 * - Cash Out: removing cash from the drawer (e.g. bank deposit, expenses)
 *
 * Movements are stored in PosProfileContext and factored into
 * the shift closing reconciliation.
 */
export default function CashMovementDialog({ open, onClose }) {
  const { cashMovements, addCashMovement, removeCashMovement, cashMovementNet } = usePosProfile();
  const [type, setType] = useState('in');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const handleAdd = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return;

    addCashMovement({ type, amount: amt, note });
    setAmount('');
    setNote('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceWalletIcon />
          <span>Cash Movements</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {/* Add new movement */}
        <Stack spacing={2} sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, v) => v && setType(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="in" color="success">
              <AddCircleOutlineIcon sx={{ mr: 0.5 }} /> Cash In
            </ToggleButton>
            <ToggleButton value="out" color="error">
              <RemoveCircleOutlineIcon sx={{ mr: 0.5 }} /> Cash Out
            </ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={handleKeyDown}
              inputProps={{ min: 0, step: '0.01' }}
              sx={{ width: 140 }}
              autoFocus
            />
            <TextField
              size="small"
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" onClick={handleAdd} disabled={!amount || parseFloat(amount) <= 0}>
              Add
            </Button>
          </Stack>
        </Stack>

        <Divider />

        {/* Movement history */}
        {cashMovements.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No cash movements recorded this shift
          </Typography>
        ) : (
          <>
            <List dense>
              {cashMovements.map((m) => (
                <ListItem key={m.id} sx={{ px: 0 }}>
                  <Chip
                    label={m.type === 'in' ? 'IN' : 'OUT'}
                    size="small"
                    color={m.type === 'in' ? 'success' : 'error'}
                    sx={{ mr: 1.5, minWidth: 40 }}
                  />
                  <ListItemText
                    primary={`${m.amount.toFixed(2)}${m.note ? ` — ${m.note}` : ''}`}
                    secondary={new Date(m.createdAt).toLocaleTimeString()}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" size="small" onClick={() => removeCashMovement(m.id)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Divider sx={{ my: 1 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2">Net Cash Adjustment</Typography>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                color={cashMovementNet >= 0 ? 'success.main' : 'error.main'}
              >
                {cashMovementNet >= 0 ? '+' : ''}{cashMovementNet.toFixed(2)}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
